import { ChevronDown, FolderOpen } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { useProjects } from '@/shared/hooks/useProjects';
import { useUIStore } from '@/core/stores/ui.store';

/**
 * Selector de proyecto activo para el contexto de configuración.
 *
 * El proyecto seleccionado se persiste en `UIStore.selectedProjectId` (UI state),
 * no en TanStack Query, porque es una preferencia de navegación del usuario
 * dentro del panel de settings — no un recurso del servidor.
 *
 * @remarks
 * Requiere `activeOrganizationId` en el store para cargar la lista de proyectos.
 * Si no hay organización activa, la query queda deshabilitada y se muestra
 * el trigger vacío sin realizar peticiones.
 */
export function ProjectSwitcher() {
  const { t } = useTranslation('settings');
  const selectedProjectId = useUIStore((s) => s.selectedProjectId);
  const setSelectedProjectId = useUIStore((s) => s.setSelectedProjectId);

  const { data: projects = [], isLoading } = useProjects();

  const selectedProject = projects.find((p) => p.id === selectedProjectId) ?? null;

  return (
    <div className="space-y-4" data-testid="settings-project-switcher">
      <div>
        <p className="text-sm font-medium text-foreground mb-2">{t('project.switcherLabel')}</p>

        {isLoading ? (
          <Skeleton className="h-10 w-full rounded-lg" data-testid="settings-project-loading" />
        ) : (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                data-testid="settings-project-trigger"
                className="flex items-center gap-2 w-full px-3 py-2.5 rounded-lg border border-border bg-muted/30 hover:bg-muted transition-colors text-left group"
              >
                <FolderOpen className="w-4 h-4 text-muted-foreground shrink-0" />
                <span className="flex-1 text-sm truncate">
                  {selectedProject ? selectedProject.name : t('project.noProject')}
                </span>
                <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0 group-hover:text-foreground transition-colors" />
              </button>
            </DropdownMenuTrigger>

            <DropdownMenuContent align="start" className="w-64">
              {projects.length === 0 ? (
                <div
                  className="px-3 py-4 text-center text-sm text-muted-foreground"
                  data-testid="settings-project-empty"
                >
                  {t('project.noProject')}
                </div>
              ) : (
                projects.map((project) => (
                  <DropdownMenuItem
                    key={project.id}
                    onClick={() => setSelectedProjectId(project.id)}
                    className="flex items-center gap-3 py-2"
                    data-testid={`settings-project-option-${project.id}`}
                  >
                    <span
                      className="w-2 h-2 rounded-full shrink-0"
                      style={{ backgroundColor: project.color ?? undefined }}
                    />
                    <span className="flex-1 truncate">{project.name}</span>
                    {project.id === selectedProjectId && (
                      <span className="w-1.5 h-1.5 rounded-full bg-primary shrink-0" />
                    )}
                  </DropdownMenuItem>
                ))
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </div>
  );
}
